import React from 'react';
import { TrendingUp, Target } from 'lucide-react';
import type { BarberGrowthSnapshot } from '../../services/barberDataService';
import { projectAtOccupancy } from '../../shared/growth';

/**
 * Capacity & Projection — surfaces the (previously-unbuilt-into-UI) projectAtOccupancy engine:
 * current occupancy %, and "at 80% / 85% you'd earn $X/yr" holding $/booked-hour constant. Drives the
 * owner's coaching + the barber's aspiration to fill the book. Null-safe (needs occupancy data, which is
 * written on the next Growth recompute). BEHAVIOR-PIN: SLIK-BARBER-PROJECTION-INCENTIVES.md · engine shared/growth.ts.
 */
const money = (n: number) => `$${Math.round(n).toLocaleString()}`;

export const GrowthProjectionCard: React.FC<{ growth?: BarberGrowthSnapshot }> = ({ growth }) => {
  const occ = growth?.occupancyPct;
  const availHrs = growth?.availableHoursPerWeek;
  const weeklyRev = growth?.weeklyServiceRevenue;
  // Needs occupancy inputs (present after a recompute). Bail cleanly if not there yet.
  if (occ == null || !availHrs || weeklyRev == null) return null;

  const bookedHrs = availHrs * (occ / 100);
  const revPerBookedHr = bookedHrs > 0 ? weeklyRev / bookedHrs : 0;
  const currentAnnual = weeklyRev * 52;
  const p80 = projectAtOccupancy(availHrs, revPerBookedHr, 80);
  const p85 = projectAtOccupancy(availHrs, revPerBookedHr, 85);
  const upside = Math.max(0, p85.annual - currentAnnual);

  const barColor = occ >= 80 ? 'bg-green-500' : occ >= 65 ? 'bg-amber-500' : 'bg-rose-500';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
        <TrendingUp className="h-5 w-5 text-indigo-600 dark:text-indigo-400" /> Capacity &amp; Projection
      </h3>

      {/* current occupancy */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-gray-500 dark:text-gray-400">Current occupancy</span>
          <span className="text-2xl font-bold text-gray-900 dark:text-white">{occ.toFixed(0)}%</span>
        </div>
        <div className="mt-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, occ)}%` }} />
        </div>
        <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
          {bookedHrs.toFixed(0)} of {availHrs.toFixed(0)} schedulable hrs/wk booked · {money(revPerBookedHr)}/booked-hr
        </div>
      </div>

      {/* projections at 80 / 85 */}
      <div className="grid grid-cols-2 gap-3">
        {[{ t: 80, p: p80 }, { t: 85, p: p85 }].map(({ t, p }) => (
          <div key={t} className={`rounded-lg p-3 border ${occ >= t ? 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-800' : 'border-indigo-200 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-800'}`}>
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
              <Target className="h-3.5 w-3.5" /> at {t}% occupancy
            </div>
            <div className="text-lg font-bold text-gray-900 dark:text-white">{money(p.annual)}<span className="text-xs font-normal text-gray-400">/yr</span></div>
            <div className="text-xs text-gray-400 dark:text-gray-500">{money(p.weekly)}/wk</div>
          </div>
        ))}
      </div>

      {/* the upside hook */}
      <div className="mt-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-3">
        {upside > 0 ? (
          <p className="text-sm">
            Filling the book to <b>85%</b> is about <b>{money(upside)}/yr</b> more than today
            {' '}(<span className="opacity-90">now ~{money(currentAnnual)}</span>).
          </p>
        ) : (
          <p className="text-sm">Running at or above target occupancy — strong. Hold the line and grow $/booked-hour next.</p>
        )}
      </div>
    </div>
  );
};

export default GrowthProjectionCard;
